import Link from 'next/link';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { Text } from '@calm-web/design-system';

import chevronIcon from 'icons/chevron-left-aligned.svg';

// Matches Android's AssociationView (view_association.xml) exactly: no background fill —
// the row sits directly on the page, with only vertical padding (fixed_half_margin, 4dp) —
// and the chevron pinned to the far right via justify-content: space-between, with the
// icon+text grouped on the left (LeftGroup) so that space has somewhere to go.
// Capped at 480px to match PackRowList's own row-list precedent (RowContainer, PlaylistInfo
// max-width: 480px) — mobile's "full width" is inherently a phone-proportioned column, so
// the row's stretch-to-fill/chevron-at-the-end behavior needs an equivalent cap on desktop's
// much wider content column.
const rowLayoutCSS = `
	align-items: center;
	display: flex;
	justify-content: space-between;
	max-width: 480px;
	width: 100%;
`;

// A real navigation link (to the linked profile), not a static label — matches the
// chevron-suffixed row convention PackClassLink already uses for "this text goes somewhere".
// Only used when `href` is passed; see RowStatic for the non-linked case.
export const Row = styled(Link)`
	${rowLayoutCSS}
	text-decoration: none;

	&:hover {
		opacity: 0.8;
	}
`;

export const RowStatic = styled.div`
	${rowLayoutCSS}
`;

// iOS CatalogBylineRow: HStack(spacing: 12) between the avatar and the labels.
export const LeftGroup = styled.div`
	align-items: center;
	display: flex;
	gap: 12px;
	min-width: 0;
`;

// iOS Avatar small preset, 44pt.
export const ImageWrapper = styled.div`
	border-radius: 50%;
	flex-shrink: 0;
	height: 44px;
	overflow: hidden;
	position: relative;
	width: 44px;
`;

// iOS CatalogBylineRow: VStack(alignment: .leading, spacing: 2).
export const TextColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

// iOS `.subhead` is 13pt semibold.
export const Title = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
	noMargin: true,
})`
	color: ${palette('white')};
	font-size: 13px;
	font-weight: 600;
	line-height: 1.2;
`;

// iOS `.caption1` (12pt regular) at 0.85 opacity, one line.
export const Subtitle = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
	noMargin: true,
})`
	color: ${palette('white')};
	font-size: 12px;
	font-weight: 400;
	line-height: 1.2;
	opacity: 0.85;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// iOS draws the chevron in the `.subhead` class at 0.66 opacity with no trailing inset. The
// asset is square, so it is held at 12px rather than squashed.
export const Chevron = styled(chevronIcon)`
	flex-shrink: 0;
	height: 12px;
	margin-left: 12px;
	opacity: 0.66;
	transform: rotate(180deg);
	width: 12px;

	& path {
		fill: ${palette('white')};
	}
`;
